// ============================================
// Premier Case Tracker — API Client
// ============================================

const envUrl = import.meta.env.VITE_API_URL;
const BASE_URL = envUrl ? envUrl.replace(/\/$/, '') : '/api';

function getAuthHeaders(): Record<string, string> {
  const token = sessionStorage.getItem('pct_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  const data = await res.json();
  if (!res.ok) {
    if (res.status === 401) {
      sessionStorage.removeItem('pct_token');
      sessionStorage.removeItem('pct_user');
      window.location.href = '/login';
    }
    throw new Error(data.error || data.message || `Request failed with status ${res.status}`);
  }
  return data as T;
}
const inFlightGets = new Map<string, Promise<any>>();

export const api = {
  get: async <T = any>(path: string): Promise<T> => {
    if (inFlightGets.has(path)) {
      return inFlightGets.get(path) as Promise<T>;
    }

    const separator = path.includes('?') ? '&' : '?';
    const cacheBuster = `_t=${Date.now()}`;
    
    const promise = fetch(`${BASE_URL}${path}${separator}${cacheBuster}`, { 
      headers: { ...getAuthHeaders(), 'Pragma': 'no-cache' },
      cache: 'no-store' 
    }).then(res => handleResponse<T>(res)).finally(() => {
      inFlightGets.delete(path);
    });

    inFlightGets.set(path, promise);
    return promise;
  },

  post: async <T = any>(path: string, body?: any): Promise<T> => {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Pragma': 'no-cache' },
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
    return handleResponse<T>(res);
  },

  put: async <T = any>(path: string, body?: any): Promise<T> => {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Pragma': 'no-cache' },
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
    return handleResponse<T>(res);
  },

  delete: async <T = any>(path: string, body?: any): Promise<T> => {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Pragma': 'no-cache' },
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
    return handleResponse<T>(res);
  },
};
